import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { rackPhotosAPI } from '../services/api';

/* Photo grid + full-screen viewer, shared by the manager's Rack Photos page and the
   public share page. Tiles use the server's 640 px thumbnails; the viewer loads the
   full 2048 px photo only when opened, which matters on the farm's connection. */

export const IST = 19800;
export const clock = ts => {
  const d = new Date((ts + IST) * 1000);
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'am' : 'pm'}`;
};
export const dayLabel = day => {
  const d = new Date(`${day}T00:00:00+05:30`);
  const today = new Date(Date.now() + IST * 1000).toISOString().slice(0, 10);
  const yest = new Date(Date.now() + (IST - 86400) * 1000).toISOString().slice(0, 10);
  const long = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
  if (day === today) return `Today · ${long}`;
  if (day === yest) return `Yesterday · ${long}`;
  return long;
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(${p => p.$min || 150}px, 100%), 1fr));
  gap: 6px;
  /* minmax(0, ...): a bare 1fr column grows to its image's natural width and overflows */
  @media (max-width: 600px) { grid-template-columns: repeat(${p => p.$phoneCols || 3}, minmax(0, 1fr)); gap: 4px; }
`;

const Tile = styled.button`
  position: relative; padding: 0; border: 0; cursor: zoom-in;
  width: 100%; min-width: 0; aspect-ratio: 1; overflow: hidden; border-radius: 8px;
  background: ${p => p.theme.colors.surfaceHover};
  img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.2s; }
  &:hover img { transform: scale(1.03); }
  &:focus-visible { outline: 3px solid ${p => p.theme.colors.primary}; outline-offset: 2px; }
  .more {
    position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
    background: rgba(0, 0, 0, 0.55); color: #fff; font-size: 20px; font-weight: 600;
  }
`;

export function PhotoGrid({ photos, onOpen, limit, min, phoneCols }) {
  const shown = limit ? photos.slice(0, limit) : photos;
  const hidden = photos.length - shown.length;
  return (
    <Grid $min={min} $phoneCols={phoneCols}>
      {shown.map((p, i) => (
        <Tile key={p.id} type="button" onClick={() => onOpen(i)} aria-label={`Open photo ${i + 1} of ${photos.length}`}>
          <img src={rackPhotosAPI.src(p.thumb)} alt="" loading="lazy" />
          {hidden > 0 && i === shown.length - 1 && <span className="more">+{hidden + 1}</span>}
        </Tile>
      ))}
    </Grid>
  );
}

const Shade = styled.div`
  /* Opaque: at 94% the page's headline ghosted through behind the photo. */
  position: fixed; inset: 0; z-index: 2000; background: #000;
  display: flex; flex-direction: column; color: #fff; touch-action: pan-y;
  .bar { display: flex; align-items: center; gap: 12px; padding: 12px 16px; font-size: 14px; }
  .bar .title { font-weight: 600; }
  .bar .sub { color: rgba(255, 255, 255, 0.65); }
  .bar .grow { flex: 1; }
  .stage { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; position: relative; }
  .stage img { max-width: 100%; max-height: 100%; object-fit: contain; user-select: none; }
  .note { padding: 10px 16px 16px; text-align: center; color: rgba(255, 255, 255, 0.8); font-size: 14px; min-height: 20px; }
`;

const Btn = styled.button`
  background: rgba(255, 255, 255, 0.12); color: #fff; border: 0; border-radius: 999px;
  min-width: 40px; height: 40px; padding: 0 14px; font: inherit; font-size: 15px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; text-decoration: none;
  &:hover { background: rgba(255, 255, 255, 0.22); }
  &:focus-visible { outline: 2px solid #fff; }
  &.danger { background: rgba(230, 57, 70, 0.85); }
`;

const Nav = styled(Btn)`
  position: absolute; top: 50%; transform: translateY(-50%); height: 56px; width: 44px; font-size: 22px;
  ${p => (p.$side === 'left' ? 'left: 10px;' : 'right: 10px;')}
  @media (max-width: 600px) { display: none; }
`;

/* Full-screen viewer. Arrow keys / swipe to move, Esc to close. `onDelete` (manager
   page only) shows a delete button and gets the photo; it confirms first. */
export function Lightbox({ photos, index, title, onClose, onIndex, onDelete }) {
  const photo = photos[index];
  const [busy, setBusy] = useState(false);
  const touch = useRef(null);
  const go = useCallback(step => onIndex((index + step + photos.length) % photos.length), [index, photos.length, onIndex]);

  useEffect(() => {
    const key = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', key);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', key); document.body.style.overflow = prev; };
  }, [go, onClose]);

  // Warm the neighbours so a swipe shows the next photo at once instead of waiting a
  // round trip for it. The browser cache does the rest.
  useEffect(() => {
    [index + 1, index - 1].forEach(i => {
      const p = photos[(i + photos.length) % photos.length];
      if (p) { const img = new Image(); img.src = rackPhotosAPI.src(p.medium || p.url); }
    });
  }, [index, photos]);

  if (!photo) return null;
  return (
    <Shade role="dialog" aria-modal="true" aria-label={title}
      onTouchStart={e => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={e => {
        const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
      }}>
      <div className="bar">
        <div className="grow">
          <div className="title">{title}</div>
          <div className="sub">{index + 1} of {photos.length}{photo.uploaded_at ? ` · ${clock(photo.uploaded_at)}` : ''}</div>
        </div>
        <Btn as="a" href={rackPhotosAPI.src(photo.url)} target="_blank" rel="noreferrer" title="Open the full-size photo">Full size</Btn>
        {onDelete && (
          <Btn className="danger" disabled={busy} onClick={async () => {
            if (!window.confirm('Delete this photo? This cannot be undone.')) return;
            setBusy(true);
            try { await onDelete(photo); } finally { setBusy(false); }
          }}>Delete</Btn>
        )}
        <Btn onClick={onClose} aria-label="Close">✕</Btn>
      </div>
      <div className="stage">
        {photos.length > 1 && <Nav $side="left" onClick={() => go(-1)} aria-label="Previous photo">‹</Nav>}
        {/* An opened photo is one someone wants to inspect, so it is always the stored
            2048 px (pinch-zoom on 1280 lost stem hairs and leaf texture). The smaller
            copies, usually cached already, sit behind it until it arrives. */}
        <img key={photo.id} alt="" decoding="async"
          src={rackPhotosAPI.src(photo.url)}
          style={{
            backgroundImage: [photo.medium, photo.thumb].filter(Boolean).map(u => `url(${rackPhotosAPI.src(u)})`).join(', '),
            backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center',
          }} />
        {photos.length > 1 && <Nav $side="right" onClick={() => go(1)} aria-label="Next photo">›</Nav>}
      </div>
      <div className="note">{photo.note || ''}</div>
    </Shade>
  );
}
